// Admin Dashboard Page
import React, { useState, useEffect } from 'react';
import { useAdminTranslation } from '../utils/i18nContext';
import { adminApi } from '../utils/api';
import KPICard from '../components/KPICard';
import { Users, Activity, Zap, Brain } from 'lucide-react';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const COLORS = ['#00D2FF', '#A855F7'];

const Dashboard = () => {
  const { t } = useAdminTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const result = await adminApi.getDashboard();
      setData(result);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400 py-12">{t('loading')}</div>;
  }

  if (!data) {
    return <div className="text-center text-gray-400 py-12">{t('error')}</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-white">{t('overview')}</h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={t('total_users')}
          value={data.kpis.total_users}
          icon={Users}
          color="text-cyan-400"
        />
        <KPICard
          title={t('users_7d')}
          value={data.kpis.users_7d}
          icon={Activity}
          color="text-purple-400"
        />
        <KPICard
          title={t('cerebro_events')}
          value={data.kpis.cerebro_events}
          icon={Brain}
          color="text-green-400"
        />
        <KPICard
          title={t('robomesh_events')}
          value={data.kpis.robomesh_events}
          icon={Zap}
          color="text-orange-400"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visits Over Time */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">{t('visits_over_time')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.charts.visits_over_time}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" stroke="#888" tick={{ fill: '#888' }} />
                <YAxis stroke="#888" tick={{ fill: '#888' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="visits"
                  stroke="#00D2FF"
                  strokeWidth={2}
                  dot={{ fill: '#00D2FF' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Mode Split */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">{t('mode_split')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.charts.mode_split}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.charts.mode_split.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
